import React, { useState, useEffect } from 'react';
import { macroNames, macroShortNames, getMacroColor } from '../styles/theme';
import {
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  LinearProgress,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
  Checkbox,
  Divider,
  Fade
} from '@mui/material';
import {
  AutoFixHigh as AIIcon,
  SwapHoriz as SwapIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  InfoOutlined as InfoIcon,
  TrendingUp as DifficultyIcon,
  AccessTime as TimeIcon,
  Psychology as BrainIcon,
  AutoAwesome as SparkleIcon,
  Inventory2 as StockIcon,
  AcUnit as FreezerIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { startOfWeek, format, addDays } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import './MenuGeneratorIA.css';

// Keyframes for custom "breathing" gradient or pulse could be added in CSS,
// using inline styles for the "Thinking" animation for now.

const LoadingExperience = ({ title, subtitle, steps, activeIndex }) => {
  const safeSteps = steps?.length ? steps : ['Procesando solicitud'];
  const currentIndex = activeIndex % safeSteps.length;
  const progress = Math.max(14, ((currentIndex + 1) / safeSteps.length) * 92);

  return (
    <Box className="ai-loading-screen">
      <Box className="ai-loading-orb">
        <CircularProgress size={96} thickness={2.2} sx={{ color: '#4f46e5' }} />
        <Box className="ai-loading-orb-center">
          <BrainIcon sx={{ fontSize: 42, color: '#4f46e5' }} />
        </Box>
      </Box>
      <Typography variant="h5" fontWeight="800" gutterBottom color="#1e293b" textAlign="center">
        {title}
      </Typography>
      <Typography color="text.secondary" align="center" maxWidth={520}>
        {subtitle}
      </Typography>

      <Box sx={{ width: '100%', maxWidth: 560, mt: 4 }}>
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 9,
            borderRadius: 999,
            bgcolor: '#e2e8f0',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              background: 'linear-gradient(90deg, #4f46e5 0%, #14b8a6 58%, #f59e0b 100%)',
              transition: 'transform 0.8s ease, width 0.8s ease'
            }
          }}
        />
        <Box className="ai-loading-steps">
          {safeSteps.map((step, index) => (
            <Box
              key={step}
              className={`ai-loading-step ${index === currentIndex ? 'active' : ''} ${index < currentIndex ? 'done' : ''}`}
            >
              <span className="ai-loading-step-dot" />
              <Typography variant="body2" fontWeight={index === currentIndex ? 700 : 500}>
                {step}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const InlineTooltip = ({ title }) => (
  <Tooltip title={title} arrow placement="top">
    <InfoIcon
      sx={{
        fontSize: 16,
        color: '#64748b',
        cursor: 'help',
        verticalAlign: 'middle',
        ml: 0.5
      }}
    />
  </Tooltip>
);

const PreferenceLabel = ({ title, tooltip, caption }) => (
  <Box>
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography variant="body2" fontWeight="600">{title}</Typography>
      <InlineTooltip title={tooltip} />
    </Box>
    <Typography variant="caption" color="text.secondary">
      {caption}
    </Typography>
  </Box>
);

const CompactFieldLabel = ({ title, tooltip }) => (
  <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {title}
    </Typography>
    <InlineTooltip title={tooltip} />
  </Box>
);

const MenuGeneratorIA = ({ onClose, onMenuSaved, weeklyData, initialWeekStart }) => {
  const { authToken } = useAuth();
  const [currentStep, setCurrentStep] = useState('setup'); // setup, generating, editing, saving
  const [weekStartDate, setWeekStartDate] = useState(() => {
    // Use passed initialWeekStart if available, otherwise default to current week
    if (initialWeekStart) {
      return startOfWeek(new Date(initialWeekStart), { weekStartsOn: 1 });
    }
    const today = new Date();
    return startOfWeek(today, { weekStartsOn: 1 });
  });
  const [keepExisting, setKeepExisting] = useState(false);
  const [includeSnacks, setIncludeSnacks] = useState(true);
  const [optimizeBatchCooking, setOptimizeBatchCooking] = useState(false);
  const [batchExtraPortions, setBatchExtraPortions] = useState(2);
  const [includeStock, setIncludeStock] = useState(false);
  const [stockItems, setStockItems] = useState([]);
  const [selectedStockIds, setSelectedStockIds] = useState([]);
  const [stockLoading, setStockLoading] = useState(false);
  const [menuProposal, setMenuProposal] = useState(null);
  const [saveWarning, setSaveWarning] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [error, setError] = useState('');
  const [selectedRecipeForChange, setSelectedRecipeForChange] = useState(null);
  const [alternatives, setAlternatives] = useState([]);
  const [isLoadingAlternatives, setIsLoadingAlternatives] = useState(false);
  const [showAlternativesDialog, setShowAlternativesDialog] = useState(false);

  const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

  // Prepare existing menu data for backend (Memoized for UI diffing)
  const existingMenuStructure = React.useMemo(() => {
    if (!weeklyData) return null;
    const result = {};
    days.forEach((dayName, index) => {
      const d = addDays(weekStartDate, index);
      const dateKey = format(d, 'yyyy-MM-dd');
      const dayData = weeklyData[dateKey];

      if (dayData && dayData.meals) {
        const dayMeals = {};
        Object.keys(dayData.meals).forEach(mealName => {
          const items = dayData.meals[mealName]?.items || [];
          // NOW: Include ALL items (recipes and ingredients) with metadata
          // We map them to an object structure the backend helper expects
          dayMeals[mealName] = items.map(i => ({
            _id: i.type === 'planned-stock' ? (i.sourceRecipeId || i._id) : i._id,
            isRecipe: i.type === 'planned-stock' ? !!i.sourceRecipeId : i.isRecipe,
            quantity: i.quantity,
            unit: i.unit,
            name: i.name // Helpful for debugging/logging
          }));
        });
        result[dayName] = dayMeals;
      } else {
        result[dayName] = { "Desayuno": [], "Media mañana": [], "Comida": [], "Media tarde": [], "Cena": [] };
      }
    });
    return result;
  }, [weeklyData, weekStartDate]);
  const getExistingMenuForBackend = () => existingMenuStructure;
  const getBatchCookingOptions = () => ({
    desiredExtraPortions: Math.max(0, Number(batchExtraPortions) || 0),
    keepExisting,
    includeSnacks
  });
  const getBatchCookingSaveOptions = () => ({
    ...getBatchCookingOptions(),
    generateAiCookSessionOnSave: true
  });

  const generationSteps = React.useMemo(() => ([
    'Leyendo tu perfil nutricional',
    'Seleccionando recetas compatibles',
    'Revisando coherencia culinaria',
    optimizeBatchCooking ? 'Buscando sinergias para batch cooking' : 'Ajustando objetivos diarios',
    'Validando la propuesta final'
  ]), [optimizeBatchCooking]);

  const savingSteps = React.useMemo(() => (
    optimizeBatchCooking
      ? [
          'Guardando menú semanal',
          'Creando plan batch cooking',
          'Generando sesión IA de cocina',
          'Preparando raciones y avisos'
        ]
      : [
          'Guardando menú semanal',
          'Actualizando calendario',
          'Preparando resumen'
        ]
  ), [optimizeBatchCooking]);

  useEffect(() => {
    if (!isGenerating && !isSaving) return undefined;

    setLoadingStepIndex(0);
    const interval = setInterval(() => {
      const stepCount = isSaving ? savingSteps.length : generationSteps.length;
      setLoadingStepIndex((previous) => (previous + 1) % Math.max(stepCount, 1));
    }, 1700);

    return () => clearInterval(interval);
  }, [isGenerating, isSaving, generationSteps, savingSteps]);

  // Cargar recetas disponibles en el fondo (solo las que tienen receta de origen)
  useEffect(() => {
    if (!authToken) return undefined;
    let cancelled = false;

    const loadStock = async () => {
      setStockLoading(true);
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/food-stock`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) throw new Error('Error cargando fondo');
        const wrapper = await res.json();
        const mapped = (wrapper.data || [])
          .map(item => {
            const recipe = item.sourceRecipe && typeof item.sourceRecipe === 'object' ? item.sourceRecipe : null;
            const recipeId = recipe?._id || (typeof item.sourceRecipe === 'string' ? item.sourceRecipe : null);
            if (!recipeId) return null; // sin receta de origen no se puede ubicar en el menú
            const macros = (item.macrosPerPortion && Object.values(item.macrosPerPortion).some(v => Number(v) > 0))
              ? item.macrosPerPortion
              : (recipe?.totalMacronutrients || {});
            return {
              stockId: item._id,
              recipeId,
              name: item.name || recipe?.name || 'Receta del fondo',
              storage: item.storage || 'freezer',
              portionsAvailable: Number(item.portionsAvailable) || 0,
              macros,
              momentoDelDia: recipe?.momentoDelDia || [],
              tipoPlato: recipe?.tipoPlato || [],
            };
          })
          .filter(item => item && item.portionsAvailable > 0);

        if (!cancelled) setStockItems(mapped);
      } catch (e) {
        console.error('Error cargando fondo:', e);
        if (!cancelled) setStockItems([]);
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    };

    loadStock();
    return () => { cancelled = true; };
  }, [authToken]);

  const toggleStockSelection = (stockId) => {
    setSelectedStockIds(prev => (
      prev.includes(stockId) ? prev.filter(id => id !== stockId) : [...prev, stockId]
    ));
  };

  const getSelectedStockPayload = () => {
    if (!includeStock) return [];
    const selected = selectedStockIds.length
      ? stockItems.filter(item => selectedStockIds.includes(item.stockId))
      : [];
    return selected;
  };

  const handleGenerateProposal = async () => {
    setIsGenerating(true);
    setLoadingStepIndex(0);
    setError('');
    setSaveWarning('');
    setSavedBatchPlan(null);
    // Prepare payload
    const payload = {
      weekStartDate: format(weekStartDate, 'yyyy-MM-dd'),
      keepExisting: keepExisting,
      includeSnacks: includeSnacks,
      existingMenu: keepExisting ? getExistingMenuForBackend() : null,
      optimizeBatchCooking,
      batchCookingOptions: optimizeBatchCooking ? getBatchCookingOptions() : undefined,
      selectedStock: getSelectedStockPayload()
    };
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/menu-generator/generate-proposal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Error generando propuesta');
      }
      const result = await response.json();
      setMenuProposal(result.data);
      setCurrentStep('editing');
    } catch (error) {
      console.error('Error generando propuesta:', error);
      setError(error.message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleFindAlternatives = async (recipeId, day, meal) => {
    setIsLoadingAlternatives(true);
    setSelectedRecipeForChange({ recipeId, day, meal });
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/menu-generator/find-alternatives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          recipeIdToReplace: recipeId,
          day,
          meal,
          currentMenuProposal: menuProposal,
          userProfile: menuProposal.userProfile
        }),
      });
      if (!response.ok) {
        throw new Error('Error buscando alternativas');
      }
      const result = await response.json();
      setAlternatives(result.data.alternatives);
      setShowAlternativesDialog(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoadingAlternatives(false);
    }
  };

  const handleReplaceRecipe = (newRecipeId) => {
    if (!selectedRecipeForChange) return;
    const { day, meal, recipeId: oldRecipeId } = selectedRecipeForChange;
    const currentMealRecipes = menuProposal?.menuSemanal[day][meal];
    let newMealRecipes;
    if (Array.isArray(currentMealRecipes)) {
      const index = currentMealRecipes.indexOf(oldRecipeId);
      if (index !== -1) {
        newMealRecipes = [...currentMealRecipes];
        newMealRecipes[index] = newRecipeId;
      } else {
        newMealRecipes = [...currentMealRecipes, newRecipeId];
      }
    } else {
      newMealRecipes = [newRecipeId];
    }
    setMenuProposal(prev => ({
      ...prev,
      menuSemanal: {
        ...prev.menuSemanal,
        [day]: {
          ...prev.menuSemanal[day],
          [meal]: newMealRecipes
        }
      }
    }));
    setShowAlternativesDialog(false);
    setSelectedRecipeForChange(null);
    setAlternatives([]);
  };

  const handleSaveMenu = async () => {
    setIsSaving(true);
    setLoadingStepIndex(0);
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/menu-generator/save-final-menu`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          finalMenuProposal: menuProposal,
          weekStartDate: format(weekStartDate, 'yyyy-MM-dd'),
          optimizeBatchCooking,
          batchCookingOptions: optimizeBatchCooking ? getBatchCookingSaveOptions() : undefined
        }),
      });
      if (!response.ok) throw new Error('Error guardando menú');
      const result = await response.json();
      const batchCookingRequested = !!result.batchCookingRequested;
      // Si se pidió batch cooking, el padre abre el wizard de selección (rango L-V).
      if (onMenuSaved) onMenuSaved({ batchCookingRequested, weekStartDate: format(weekStartDate, 'yyyy-MM-dd') });
      if (batchCookingRequested) {
        // No mostramos paso intermedio: el wizard de batch cooking toma el relevo.
        return;
      }
      setSaveWarning('');
      setCurrentStep('completed');
      setTimeout(() => { if (onClose) onClose(); }, 2000);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Helpers
  const getRecipeDetails = (id) => menuProposal?.recipeDetails?.[id];
  const formatPrepTime = (pt) => {
    if (!pt) return '';
    const { hours, minutes } = pt;
    return (hours ? `${hours}h ` : '') + (minutes ? `${minutes}m` : '');
  };
  const getDifficultyColor = (d) => d === 'Baja' ? 'success' : d === 'Media' ? 'warning' : 'error';

  // Helper macro calc
  const calculateDayMacros = (dayMeals) => {
    let kilocalories = 0;
    let protein = 0;
    let carbs = 0;
    let fat = 0;

    Object.values(dayMeals).forEach(mealIds => {
      if (!Array.isArray(mealIds)) return;
      mealIds.forEach(item => {
        // item could be object (ingredient/existing) or string (new recipe)
        if (typeof item === 'object') {
          // Extract macros from embedded object (existing item)
          const itemMacros = item.macros;
          if (itemMacros) {
            kilocalories += Number(itemMacros.kilocalories || itemMacros.k) || 0;
            protein += Number(itemMacros.protein || itemMacros.p) || 0;
            carbs += Number(itemMacros.carbs || itemMacros.c) || 0;
            fat += Number(itemMacros.fat || itemMacros.f) || 0;
          }
        } else {
          const r = getRecipeDetails(item);
          if (r && r.macros) {
            kilocalories += Number(r.macros.kilocalories) || 0;
            protein += Number(r.macros.protein) || 0;
            carbs += Number(r.macros.carbs) || 0;
            fat += Number(r.macros.fat) || 0;
          }
        }
      });
    });
    return { kilocalories, protein, carbs, fat };
  };

  // Rendering
  if (isGenerating) {
    return (
      <LoadingExperience
        title="Diseñando tu menú..."
        subtitle="La IA está equilibrando nutrición, recetas disponibles y coherencia culinaria para que el menú tenga sentido en la mesa."
        steps={generationSteps}
        activeIndex={loadingStepIndex}
      />
    );
  }

  if (isSaving) {
    return (
      <LoadingExperience
        title={optimizeBatchCooking ? 'Guardando y preparando batch cooking...' : 'Guardando tu menú...'}
        subtitle={optimizeBatchCooking
          ? 'Se está guardando el menú, creando el plan batch y generando la sesión IA de cocina. Puede tardar un poco más, pero ahora sabes qué está haciendo.'
          : 'Estamos dejando tu planificación semanal lista en el calendario.'}
        steps={savingSteps}
        activeIndex={loadingStepIndex}
      />
    );
  }

  if (currentStep === 'setup') {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ p: 3, borderBottom: '1px solid #e2e8f0' }}>
          <Typography variant="h5" fontWeight="800" gutterBottom sx={{ background: 'linear-gradient(45deg, #4f46e5, #ec4899)', backgroundClip: 'text', textFillColor: 'transparent', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Generador de Menús IA
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configura tus preferencias y deja que la IA planifique tu semana.
          </Typography>
        </Box>

        <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
          )}

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <TimeIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">Semana del menú</Typography>
                  <InlineTooltip title="El lunes de la semana que quieres planificar. Se usa para leer huecos ya existentes, guardar el menú y crear el plan de batch cooking si lo activas." />
                </Box>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <DatePicker
                    label="Inicio de la semana"
                    value={weekStartDate}
                    onChange={(newValue) => setWeekStartDate(newValue)}
                    renderInput={(params) => <TextField {...params} fullWidth />}
                    sx={{ width: '100%' }}
                  />
                </LocalizationProvider>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Preferencias</Typography>
                <FormControlLabel
                  control={<Switch checked={keepExisting} onChange={(e) => setKeepExisting(e.target.checked)} />}
                  label={
                    <PreferenceLabel
                      title="Mantener menú existente"
                      tooltip="Si ya tienes recetas en esa semana, la IA las deja fijas y solo rellena los huecos vacíos. Útil para completar una semana a medias sin pisar lo que ya elegiste."
                      caption="Rellena solo los huecos vacíos respetando tus recetas"
                    />
                  }
                  sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  control={<Switch checked={includeSnacks} onChange={(e) => setIncludeSnacks(e.target.checked)} />}
                  label={
                    <PreferenceLabel
                      title="Incluir snacks"
                      tooltip="Activa Media mañana y Media tarde. Si está encendido, el backend rellena esos slots aunque la IA los deje vacíos."
                      caption="Generar sugerencias para media mañana y tarde"
                    />
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                />
                <FormControlLabel
                  control={<Switch checked={optimizeBatchCooking} onChange={(e) => setOptimizeBatchCooking(e.target.checked)} />}
                  label={
                    <PreferenceLabel
                      title="Optimizar para batch cooking"
                      tooltip="Añade una capa batch al generador: prioriza recetas distintas que compartan bases, ingredientes o técnicas entre lunes y viernes, sin sacrificar objetivos nutricionales ni momentos de comida."
                      caption="La IA prioriza recetas que compartan bases, ingredientes y preparaciones sin romper tus objetivos"
                    />
                  }
                  sx={{ mt: 2, display: 'flex', alignItems: 'flex-start' }}
                />
                {optimizeBatchCooking && (
                  <Box display="flex" gap={2} mt={1} flexWrap="wrap">
                    <Box sx={{ width: 210 }}>
                      <CompactFieldLabel
                        title="Raciones extra para fondo"
                        tooltip="Cuántas raciones adicionales quieres cocinar para guardar en Fondo. Aumentan la lista de la compra y se registran en Fondo cuando marcas el plan como cocinado."
                      />
                      <TextField
                        type="number"
                        size="small"
                        value={batchExtraPortions}
                        onChange={(e) => setBatchExtraPortions(e.target.value)}
                        inputProps={{ min: 0, max: 12, 'aria-label': 'Raciones extra para fondo' }}
                        sx={{ width: '100%' }}
                      />
                    </Box>
                    <Box sx={{ width: 210, display: 'flex', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary">
                        El número ideal de bloques de preparación se optimiza automáticamente según las sinergias detectadas entre tus recetas.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper elevation={0} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 3 }}>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <StockIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight="bold">Recetas del fondo</Typography>
                  <InlineTooltip title="Reutiliza raciones que ya tienes cocinadas en nevera o congelador. La IA decidirá en qué día y comida encajan mejor y descontará el fondo al confirmar el menú." />
                </Box>
                <FormControlLabel
                  control={<Switch checked={includeStock} onChange={(e) => setIncludeStock(e.target.checked)} />}
                  label={
                    <PreferenceLabel
                      title="Incluir recetas del fondo"
                      tooltip="Si lo activas, eliges qué raciones del fondo quieres reutilizar. La IA las coloca en los huecos donde encajen y descuenta el fondo al confirmar el menú."
                      caption="Aprovecha lo que ya tienes preparado y deja que la IA lo ubique"
                    />
                  }
                  sx={{ display: 'flex', alignItems: 'flex-start' }}
                />
                {includeStock && (
                  <Box mt={1}>
                    {stockLoading ? (
                      <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>
                    ) : stockItems.length === 0 ? (
                      <Alert severity="info" sx={{ borderRadius: 2 }}>
                        No tienes recetas disponibles en el fondo. Añade raciones desde la secci&oacute;n Fondo.
                      </Alert>
                    ) : (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" color="text.secondary" fontWeight={700}>
                            Selecciona las recetas a reutilizar
                          </Typography>
                          <Button
                            size="small"
                            onClick={() => setSelectedStockIds(
                              selectedStockIds.length === stockItems.length ? [] : stockItems.map(i => i.stockId)
                            )}
                          >
                            {selectedStockIds.length === stockItems.length ? 'Quitar todas' : 'Seleccionar todas'}
                          </Button>
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        <Box sx={{ maxHeight: 240, overflowY: 'auto', pr: 1 }}>
                          {stockItems.map(item => (
                            <Box key={item.stockId} display="flex" alignItems="center" gap={1} py={0.5} borderBottom="1px dashed #f1f5f9">
                              <Checkbox
                                size="small"
                                checked={selectedStockIds.includes(item.stockId)}
                                onChange={() => toggleStockSelection(item.stockId)}
                              />
                              <Box flex={1} minWidth={0}>
                                <Typography variant="body2" fontWeight={600} color="#1e293b" noWrap>{item.name}</Typography>
                                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                                  <Chip
                                    size="small"
                                    icon={item.storage === 'freezer' ? <FreezerIcon /> : <StockIcon />}
                                    label={item.storage === 'freezer' ? 'Congelador' : 'Nevera'}
                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                  />
                                  <Typography variant="caption" color="text.secondary">{item.portionsAvailable} raciones</Typography>
                                  {item.macros?.kilocalories ? (
                                    <Typography variant="caption" color="text.secondary">&bull; {Math.round(item.macros.kilocalories)} kcal/raci&oacute;n</Typography>
                                  ) : null}
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                        {selectedStockIds.length === 0 && (
                          <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                            Marca al menos una receta para que la IA la tenga en cuenta.
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 3, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleGenerateProposal}
            startIcon={<AutoFixHighIcon />}
            sx={{
              bgcolor: '#4f46e5',
              '&:hover': { bgcolor: '#4338ca' },
              borderRadius: 2,
              py: 1.5,
              fontWeight: 'bold',
              textTransform: 'none',
              fontSize: '1.1rem'
            }}
          >
            Generar Menú Semanal
          </Button>
        </Box>
      </Box>
    );
  }

  if (currentStep === 'completed') {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" p={4}>
        <CheckIcon sx={{ fontSize: 80, color: '#10b981', mb: 2 }} />
        <Typography variant="h4" fontWeight="bold" gutterBottom>¡Listo!</Typography>
        <Typography color="text.secondary" textAlign="center">
          Tu menú ha sido guardado.
        </Typography>
        {saveWarning && (
          <Alert severity="warning" sx={{ mt: 2, maxWidth: 520 }}>
            {saveWarning}
          </Alert>
        )}
      </Box>
    );
  }
  // Calculamos resumen semanal para la cabecera sticky
  const userMacros = menuProposal?.userProfile?.macros || { kilocalories: 2000, protein: 150, carbs: 250, fat: 70 };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Loading Overlay for Alternatives */}
      <Fade in={isLoadingAlternatives}>
        <Box position="absolute" top={0} left={0} right={0} bottom={0} zIndex={10} bgcolor="rgba(255,255,255,0.8)" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" mt={2} fontWeight="bold" color="primary">Buscando mejores opciones...</Typography>
        </Box>
      </Fade>

      {/* Cabecera Sticky con Resumen Global */}
      <Paper elevation={3} sx={{ p: 2, mb: 2, borderRadius: 3, background: 'linear-gradient(to right, #ffffff, #f8fafc)', borderLeft: '6px solid #4f46e5' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h5" fontWeight="800" color="#1e293b">Tu Propuesta Semanal</Typography>
            <Typography variant="body2" color="text.secondary">
              Objetivo Diario: <strong>{userMacros.kilocalories} kcal</strong>
            </Typography>
          </Box>

          <Box display="flex" gap={2}>
            <Button onClick={() => setCurrentStep('setup')} variant="outlined" color="error" size="small">Rechazar y reintentar</Button>
            <Button
              variant="contained"
              onClick={handleSaveMenu}
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
              sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, fontWeight: 'bold' }}
            >
              Confirmar Plan
            </Button>
          </Box>
        </Box>

        {menuProposal?.batchCooking?.enabled && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Propuesta generada con contexto batch cooking. Al confirmar, se guardará el menú, se creará el plan batch y se generará una sesión IA de cocina.
          </Alert>
        )}
        {menuProposal?.menuQuality && !menuProposal.menuQuality.ok && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            La propuesta necesitó revisión automática y aún tiene avisos: {(menuProposal.menuQuality.warnings || []).join(' ')}
          </Alert>
        )}

        {menuProposal?.resumenNutricional && (
          <Grid container spacing={2} mt={1}>
            {/* Kcal */}
            <Grid item xs={6} sm={3}>
              <Box p={1.5} bgcolor="#ecfdf5" borderRadius={2} border="1px solid #a7f3d0">
                <Typography variant="caption" color="#047857" fontWeight="bold">{macroNames.kilocalories.toUpperCase()} PROMEDIO</Typography>
                <Typography variant="h6" color="#065f46" fontWeight="800">
                  {Math.round(menuProposal.resumenNutricional.promedioKcalDiarias)} <span style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>/ {userMacros.kilocalories}</span>
                </Typography>
                <Box width="100%" height={6} bgcolor="#d1fae5" borderRadius={3} mt={0.5}>
                  <Box
                    width={`${Math.min((menuProposal.resumenNutricional.promedioKcalDiarias / userMacros.kilocalories) * 100, 100)}%`}
                    height="100%"
                    bgcolor={getMacroColor('kilocalories')}
                    borderRadius={3}
                  />
                </Box>
              </Box>
            </Grid>
            {/* Protein */}
            <Grid item xs={6} sm={3}>
              <Box p={1.5} bgcolor="#eff6ff" borderRadius={2} border="1px solid #bfdbfe">
                <Typography variant="caption" color="#1e40af" fontWeight="bold">{macroNames.protein.toUpperCase()}</Typography>
                <Typography variant="h6" color="#1e3a8a" fontWeight="800">
                  {Math.round(menuProposal.resumenNutricional.promedioProteinDiario)}g <span style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>/ {userMacros.protein}g</span>
                </Typography>
                <Box width="100%" height={6} bgcolor="#dbeafe" borderRadius={3} mt={0.5}>
                  <Box
                    width={`${Math.min((menuProposal.resumenNutricional.promedioProteinDiario / userMacros.protein) * 100, 100)}%`}
                    height="100%"
                    bgcolor={getMacroColor('protein')}
                    borderRadius={3}
                  />
                </Box>
              </Box>
            </Grid>
            {/* Carbs */}
            <Grid item xs={6} sm={3}>
              <Box p={1.5} bgcolor="#fff7ed" borderRadius={2} border="1px solid #fed7aa">
                <Typography variant="caption" color="#9a3412" fontWeight="bold">{macroNames.carbs.toUpperCase()}</Typography>
                <Typography variant="h6" color="#7c2d12" fontWeight="800">
                  {Math.round(menuProposal.resumenNutricional.promedioCarbsDiarios)}g <span style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>/ {userMacros.carbs}g</span>
                </Typography>
                <Box width="100%" height={6} bgcolor="#ffedd5" borderRadius={3} mt={0.5}>
                  <Box
                    width={`${Math.min((menuProposal.resumenNutricional.promedioCarbsDiarios / userMacros.carbs) * 100, 100)}%`}
                    height="100%"
                    bgcolor={getMacroColor('carbs')}
                    borderRadius={3}
                  />
                </Box>
              </Box>
            </Grid>
            {/* Fat */}
            <Grid item xs={6} sm={3}>
              <Box p={1.5} bgcolor="#faf5ff" borderRadius={2} border="1px solid #e9d5ff">
                <Typography variant="caption" color="#6b21a8" fontWeight="bold">{macroNames.fat.toUpperCase()}</Typography>
                <Typography variant="h6" color="#581c87" fontWeight="800">
                  {Math.round(menuProposal.resumenNutricional.promedioGrasaDiaria)}g <span style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>/ {userMacros.fat}g</span>
                </Typography>
                <Box width="100%" height={6} bgcolor="#f3e8ff" borderRadius={3} mt={0.5}>
                  <Box
                    width={`${Math.min((menuProposal.resumenNutricional.promedioGrasaDiaria / userMacros.fat) * 100, 100)}%`}
                    height="100%"
                    bgcolor={getMacroColor('fat')}
                    borderRadius={3}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        )}
      </Paper>

      <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
        <Grid container spacing={2}>
          {days.map(day => {
            const dayMeals = menuProposal?.menuSemanal[day] || {};
            const dayMacros = calculateDayMacros(dayMeals);
            const kcalPercent = (dayMacros.kilocalories / (userMacros.kilocalories || 1)) * 100;

            return (
              <Grid item xs={12} md={6} xl={4} key={day}>
                <Card elevation={0} sx={{ height: '100%', border: '1px solid #e2e8f0', borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
                  {/* Day Card Header with Macros */}
                  <Box sx={{ p: 2, borderBottom: '1px solid #e2e8f0', bgcolor: kcalPercent > 110 ? '#fff1f2' : '#f8fafc' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="h6" fontWeight="bold" color="#334155">{day}</Typography>
                      <Chip label={`${Math.round(dayMacros.kilocalories)} ${macroShortNames.kilocalories.toLowerCase()}`} size="small" color={kcalPercent > 110 ? "error" : "default"} />
                    </Box>
                    {/* Mini Macro Bars */}
                    <Box display="flex" gap={0.5} sx={{ height: 4, width: '100%', borderRadius: 2, overflow: 'hidden', bgcolor: '#e2e8f0' }}>
                      <Box width={`${(dayMacros.protein / (dayMacros.protein + dayMacros.carbs + dayMacros.fat || 1)) * 100}%`} bgcolor={getMacroColor('protein')} />
                      <Box width={`${(dayMacros.carbs / (dayMacros.protein + dayMacros.carbs + dayMacros.fat || 1)) * 100}%`} bgcolor={getMacroColor('carbs')} />
                      <Box width={`${(dayMacros.fat / (dayMacros.protein + dayMacros.carbs + dayMacros.fat || 1)) * 100}%`} bgcolor={getMacroColor('fat')} />
                    </Box>
                    <Box display="flex" justifyContent="space-between" mt={0.5}>
                      <Typography variant="caption" color="#1d4ed8">{macroShortNames.protein}: {Math.round(dayMacros.protein)}g</Typography>
                      <Typography variant="caption" color="#c2410c">{macroShortNames.carbs}: {Math.round(dayMacros.carbs)}g</Typography>
                      <Typography variant="caption" color="#7e22ce">{macroShortNames.fat}: {Math.round(dayMacros.fat)}g</Typography>
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 0, flex: 1 }}>
                    {['Desayuno', 'Media mañana', 'Comida', 'Media tarde', 'Cena'].map((meal, idx) => {
                      const ids = dayMeals[meal] || [];
                      const isEmpty = ids.length === 0;

                      // Modified condition: Only hide if disabled AND empty.
                      if (!includeSnacks && (meal === 'Media mañana' || meal === 'Media tarde') && isEmpty) return null;

                      return (
                        <Box key={meal} p={2} borderBottom={idx < 4 ? "1px dashed #f1f5f9" : "none"}>
                          <Typography variant="caption" color="#64748b" fontWeight="bold" textTransform="uppercase" letterSpacing={1}>
                            {meal}
                          </Typography>
                          {isEmpty ? (
                            <Typography variant="body2" color="text.secondary" fontStyle="italic" mt={1}>
                              Sin sugerencia
                            </Typography>
                          ) : (
                            ids.map((item, recipeIdx) => {
                              // Handle Object vs String
                              const isObject = typeof item === 'object';
                              const id = isObject ? (item._id || item.id) : item;

                              const r = getRecipeDetails(id);
                              const displayName = isObject ? item.name : (r?.name || 'Cargando...');
                              const displayKcal = isObject
                                ? (item.macros?.kilocalories || item.macros?.k || (item.isRecipe && r ? r.macros?.kilocalories : 0))
                                : r?.macros?.kilocalories;

                              const isStock = isObject && item.fromStock;
                              const wasExisting = isObject && !item.fromStock;
                              const borderColor = isStock ? "3px solid #3b82f6" : wasExisting ? "3px solid #10b981" : "3px solid #e2e8f0";

                              return (
                                <Box key={recipeIdx} mt={1} pl={1} borderLeft={borderColor}>
                                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                    <Box>
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" fontWeight="600" color="#1e293b">
                                          {displayName}
                                        </Typography>
                                        {!isObject && (
                                          <Tooltip title="Sugerencia de la IA">
                                            <SparkleIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                          </Tooltip>
                                        )}
                                        {isStock && (
                                          <Tooltip title="Receta del fondo (ya cocinada)">
                                            <StockIcon sx={{ fontSize: 15, color: '#3b82f6' }} />
                                          </Tooltip>
                                        )}
                                        {wasExisting && (
                                          <Tooltip title="Tu plato existente">
                                            <CheckIcon sx={{ fontSize: 14, color: '#10b981' }} />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      <Box display="flex" gap={1} mt={0.5}>
                                        {displayKcal ? (
                                          <Typography variant="caption" color="text.secondary">
                                            {Math.round(displayKcal)} kcal
                                          </Typography>
                                        ) : null}
                                        {r?.difficulty && (
                                          <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.7 }}>
                                            • {r.difficulty}
                                          </Typography>
                                        )}
                                      </Box>
                                    </Box>
                                    {/* Only allow swapping new AI suggestions (not existing or stock) */}
                                    {!isObject && (
                                      <IconButton
                                        size="small"
                                        onClick={() => handleFindAlternatives(id, day, meal)}
                                        sx={{ color: '#94a3b8', '&:hover': { color: '#6366f1', bgcolor: '#eef2ff' } }}
                                      >
                                        <SwapIcon fontSize="small" />
                                      </IconButton>
                                    )}
                                  </Box>
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
      {/* Alternatives Dialog */}
      <Dialog open={showAlternativesDialog} onClose={() => setShowAlternativesDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 'bold' }}>Cambiar Receta</DialogTitle>
        <DialogContent dividers>
          {alternatives.length === 0 ? <Alert severity="info">Buscando opciones similares...</Alert> : (
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Hemos encontrado {alternatives.length} opciones nutricionalmente compatibles:
              </Typography>
              {alternatives.map(alt => (
                <Card key={alt.id} variant="outlined" sx={{ p: 1, '&:hover': { bgcolor: '#f8fafc', borderColor: '#6366f1' }, cursor: 'pointer' }} onClick={() => handleReplaceRecipe(alt.id)}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography fontWeight="bold" color="#334155">{alt.name}</Typography>
                      <Box display="flex" gap={1} mt={0.5}>
                        <Chip label={`${Math.round(alt.macros?.kilocalories)} kcal`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        <Chip label={alt.difficulty} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                      </Box>
                    </Box>
                    <Button size="small" variant="text">Seleccionar</Button>
                  </Box>
                </Card>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setShowAlternativesDialog(false)}>Cancelar</Button></DialogActions>
      </Dialog>
    </Box >
  );
};

// End of component logic

// Helper for AutoFixHighIcon since it was used in layout
const AutoFixHighIcon = (props) => <AIIcon {...props} />;

export default MenuGeneratorIA;
